import { AppLink } from '@/components/AppLink';
import { DraftMarker } from '@/components/DraftMarker';
import { EJOURNAL_NAME, earlierIssues, latestAvailableIssue, missingNewerIssues } from '@/data/ejournal';
import { formatSize } from '@/lib/formatSize';

const EYEBROW = 'm-0 font-mono text-[0.75rem] tracking-[0.08em] uppercase';

// Right-hand card of the notice board section. ONLY the Student's E-Journal goes
// here; circulars and notices live on the left. Everything on it comes from the
// uploaded PDF and its month, so the staff type nothing extra.
export function EJournalCard() {
  const latest = latestAvailableIssue();
  const earlier = earlierIssues(3);
  const missing = missingNewerIssues();
  return (
    <aside
      aria-labelledby="ejournal-title"
      className="box-content flex min-w-0 flex-[1_1_280px] flex-col border border-rule bg-white p-7"
    >
      {missing > 0 && (
        <DraftMarker className="mb-3.5 self-start tracking-[0.04em]">
          DRAFT · {missing} newer issue{missing > 1 ? 's are' : ' is'} not on the site yet
        </DraftMarker>
      )}
      <p className={`${EYEBROW} text-teal-700`}>Monthly · for students</p>
      <h3 id="ejournal-title" className="mt-2 mb-2 font-serif text-h3 font-semibold text-navy-900">
        {EJOURNAL_NAME}
      </h3>
      <p className="m-0 text-[1rem] leading-[1.55] text-ink-2">
        One PDF every month: technical articles by ITES members, employment news and ITES news.
      </p>

      {latest?.pdfUrl && (
        <div className="mt-5 flex gap-5 border-t border-rule pt-5">
          {latest.cover && (
            <a href={latest.pdfUrl} tabIndex={-1} aria-hidden="true" className="flex-none">
              <img
                src={latest.cover}
                alt=""
                width={440}
                height={570}
                className="block h-auto w-[132px] border border-rule-strong"
              />
            </a>
          )}
          <div className="min-w-0">
            <p className={`${EYEBROW} text-muted`}>Latest issue</p>
            <p className="mt-1.5 mb-0 font-serif text-[1.5rem] leading-[1.2] font-semibold text-navy-900">
              {latest.monthName} {latest.year}
            </p>
            <p className="mt-1 mb-0 text-[0.9375rem] text-ink-2">{latest.label}</p>
            <p className="mt-1 mb-0 font-mono text-[0.8125rem] text-muted">
              {latest.pages} pages · {formatSize(latest.sizeBytes ?? 0)}
            </p>
            <AppLink href={latest.pdfUrl} className="tap mt-3 inline-block font-semibold underline underline-offset-[3px]">
              Download PDF ↓
            </AppLink>
          </div>
        </div>
      )}

      {earlier.length > 0 && (
        <div className="mt-5">
          <p className={`${EYEBROW} mb-2 text-muted`}>Earlier issues</p>
          <ul className="m-0 list-none p-0">
            {earlier.map((i) => (
              <li key={i.id} className="border-t border-rule-subtle">
                <AppLink
                  href={i.pdfUrl ?? "/e-journal"}
                  className="flex min-h-11 items-center justify-between gap-4 py-2 text-[1rem] font-semibold text-ink no-underline hover:text-teal-700"
                >
                  <span>
                    {i.monthName} {i.year}
                    <span className="ml-2 text-[0.875rem] font-normal text-muted">{i.label}</span>
                  </span>
                  <span className="font-mono text-[0.75rem] font-medium text-muted">
                    PDF · {formatSize(i.sizeBytes ?? 0)} <span aria-hidden="true">↓</span>
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-5">
        <AppLink
          href="/e-journal"
          className="block border-t border-rule pt-3.5 font-semibold text-teal-700 no-underline max-sm:min-h-11"
        >
          Browse all issues →
        </AppLink>
      </div>
    </aside>
  );
}
